import { Box, Typography } from "@mui/material";
import { motion, useCycle } from "framer-motion";
import { type FC, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useIsHalloweenSeason } from "@/hooks/useIsHalloweenSeason";
import { useThemePreference } from "@/theme/ThemePreferenceProvider";
import { DEFAULT_LOGO, LOGO_OPTION_BY_ID } from "@/theme/tokens";
import { HalloweenLoader } from "@/ui/Seasonal/HalloweenLoader";
import { SeasonalLogoHat } from "@/ui/Seasonal/SeasonalLogoHat";

// primary-logo.png is 555 × 150 with the square mark at its left edge.
const LOADING_LOGO_MARK_RATIO = 150 / 555;

export const LoadingFallback: FC = () => {
	const { t } = useTranslation();
	const { logo } = useThemePreference();
	const isHalloweenSeason = useIsHalloweenSeason();

	const messages = useMemo(() => [t("loading.shifts"), t("preparing.schedule"), t("getting.team.ready"), t("almost.there")], [t]);

	const [currentMessage, cycleMessage] = useCycle(...messages);

	useEffect(() => {
		const interval = setInterval(cycleMessage, 2000);

		return () => clearInterval(interval);
	}, [cycleMessage]);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				justifyContent: "center",
				alignItems: "flex-start",
				height: "100%",
				gap: 3,
			}}
		>
			<SeasonalLogoHat decorated={isHalloweenSeason && logo === DEFAULT_LOGO} markWidthRatio={LOADING_LOGO_MARK_RATIO} dataTestId="loading-logo">
				<Box
					component="img"
					src={LOGO_OPTION_BY_ID[logo].loading}
					alt="Logo"
					sx={{
						alignItems: "flex-start",
						width: 240,
						height: "auto",
					}}
				/>
			</SeasonalLogoHat>
			<Box
				sx={{
					display: "flex",
					// The Halloween loaders are taller than the spinner, so they center on the message instead.
					alignItems: isHalloweenSeason ? "center" : "flex-start",
					gap: 2,
					width: 300,
					marginLeft: 2,
				}}
			>
				{isHalloweenSeason ? (
					<HalloweenLoader />
				) : (
					<motion.img
						src={LOGO_OPTION_BY_ID[logo].spinner}
						alt="Loading"
						style={{
							width: 32,
							height: 32,
							backgroundColor: "transparent",
							mixBlendMode: "multiply",
						}}
						animate={{ rotate: [0, 360, 360] }}
						transition={{
							duration: 1,
							repeat: Infinity,
							ease: "linear",
							times: [0, 0.5, 1],
						}}
					/>
				)}
				<Typography variant="body1" sx={{ color: "black", fontSize: "20px" }}>
					{currentMessage}
				</Typography>
			</Box>
		</Box>
	);
};
